import { usePptxFile } from '../../hooks/usePptxFile';
import { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardMedia,
  Grid,
  Typography,
  CircularProgress,
} from '@mui/material';
import { PptxFile, ParsedSlide } from '../../types';

interface SlidePickerProps {
  pptxFileId: string;
  onPick: (slideNumber: number) => void;
}

function SlidePicker({ pptxFileId, onPick }: SlidePickerProps) {
  const { pptxFile, loading, error } = usePptxFile(pptxFileId);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (!pptxFile) {
    return (
      <Box p={2}>
        <Typography color="error">Failed to load slides</Typography>
      </Box>
    );
  }

  return (
    <Grid container spacing={2} p={2}>
      {pptxFile.parsedSlides.map((slide: ParsedSlide) => (
        <Grid item xs={12} sm={6} md={4} lg={3} key={slide.slideNumber}>
          <Card 
            sx={{ 
              cursor: 'pointer',
              '&:hover': {
                boxShadow: theme => theme.shadows[4],
              },
            }}
            onClick={() => onPick(slide.slideNumber)}
          >
            <Box sx={{ position: 'relative', width: '100%', paddingTop: '56.25%' }}>
              <CardMedia
                component="img"
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  bgcolor: 'grey.100',
                  border: theme => `1px solid ${theme.palette.divider}`,
                }}
                image={`/api/files/thumbnails/${pptxFile._id}/${slide.slideNumber}.jpg`}
                onError={(e: React.SyntheticEvent<HTMLImageElement>) => {
                  const img = e.currentTarget;
                  if (!img.getAttribute('data-error')) {
                    img.setAttribute('data-error', 'true');
                    img.src = '/images/placeholder-slide.jpg';
                  }
                }}
                alt={`Slide ${slide.slideNumber}`}
              />
            </Box>
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>
                Slide {slide.slideNumber}
              </Typography>
              {slide.description && (
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {slide.description}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      ))}
    </Grid>
  );
}

export default SlidePicker;
